/* Quick personalization edits retain the exact line, variant, quantity and complete property map.
 * Shopify cart/change.js replaces properties; never send only the edited fields or cart/add.js.
 */
(() => {
  'use strict';
  if (customElements.get('je-personalization-cart-editor')) return;

  const propertyName = name => /^properties\[([^\]]+)\]$/.exec(name || '')?.[1];
  const fingerprint = properties => JSON.stringify(Object.entries(properties || {})
    .filter(([, value]) => value != null && String(value) !== '')
    .map(([name, value]) => [name, String(value)])
    .sort(([a], [b]) => a.localeCompare(b)));
  const planId = item => String(item.selling_plan_allocation?.selling_plan?.id || '');
  const isStudio = item => ['_Studio submission', '_Studio upgrade for', '_Design file', 'Design file', '_Engraving files', 'Engraving files', '_Script name configuration', '_Script name submission', '_Family configuration', '_Family submission']
    .some(name => item.properties?.[name]);

  customElements.define('je-personalization-cart-editor', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.key = new URLSearchParams(window.location.search).get('personalization_cart_line');
      if (!this.key) return;
      this.initialized = true;
      this.form = document.getElementById(this.dataset.formId);
      this.root = this.form?.querySelector('[data-je-root]');
      if (!this.form || !this.root) return;
      this.copy = JSON.parse(this.querySelector('[data-cart-edit-copy]').textContent);
      this.status = this.querySelector('[data-cart-edit-status]');
      this.error = this.querySelector('[data-cart-edit-error]');
      this.buttons = Array.from(this.form.elements).filter(field => field.type === 'submit');
      this.info = this.closest('.product__info-container') || this.form;
      this.info.classList.add('je-cart-edit-active');
      this.hidden = false;
      this.mode = 'loading';
      this.form.addEventListener('je:before-cart-add', event => {
        event.preventDefault();
        if (this.mode === 'ready') this.save();
        else if (this.mode === 'blocked') this.error.focus();
      });
      // The existing field validators run in capture phase. Even if Dawn's product-form
      // asset fails to load, a valid edit must never fall through to the native /cart/add form.
      this.form.addEventListener('submit', event => {
        const handled = event.defaultPrevented;
        event.preventDefault();
        if (!handled && this.mode === 'ready') this.save();
      });
      this.querySelector('a').addEventListener('click', event => {
        if (this.mode === 'saving') event.preventDefault();
      });
      this.setButtons(true, this.copy.loading);
      this.status.textContent = this.copy.loading;
      this.lockFixedChoices();
      // Keep async load from overwriting something typed before the cart arrives.
      this.setFieldsBusy(true);
      this.load();
    }

    async request(path, options = {}) {
      const root = window.Shopify?.routes?.root || '/';
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${root}${path}`, {
          credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
        });
        const body = await response.json();
        if (!response.ok || body.status >= 400) {
          const error = new Error('Cart request rejected');
          error.rejected = response.status >= 400 && response.status < 500;
          throw error;
        }
        return body;
      } finally {
        clearTimeout(timer);
      }
    }

    async load() {
      try {
        const cart = await this.request('cart.js');
        const item = cart.items?.find(line => line.key === this.key);
        if (!item) return this.block('missing');
        const variant = this.form.querySelector('[name="id"]');
        if (String(item.product_id) !== this.dataset.productId || String(item.variant_id) !== variant?.value || isStudio(item)) {
          return this.block('unsupported');
        }
        this.item = item;
        this.original = { ...item.properties };
        this.setFieldsBusy(false);
        if (!this.restore()) return this.block('unsupported');
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? ` · ${item.variant_title}` : '';
        this.querySelector('[data-cart-edit-item]').textContent = `${item.product_title || item.title}${variantTitle} · ${this.copy.quantity}: ${item.quantity}`;
        this.querySelector('[data-cart-edit-artwork]').hidden = !this.original.Logo;
        this.mode = 'ready';
        globalThis.JaxStorefrontAnalytics?.trackForm?.(this.form, 'edit_opened');
        this.status.textContent = this.copy.ready;
        this.setButtons(false, this.copy.save);
        this.querySelector('[data-cart-edit-heading]').focus({ preventScroll: true });
        this.scrollIntoView({ block: 'start', behavior: 'instant' });
      } catch (_) {
        this.block('load_error');
      }
    }

    restore() {
      this.root.dataset.jeExistingLogo = this.original.Logo ? 'true' : 'false';
      // Radio/type changes configure which property inputs the existing form submits.
      const type = this.original['Personalization type'] || 'Text';
      const radios = Array.from(this.root.querySelectorAll('[data-je-type]'));
      if (radios.length && !radios.some(field => field.value === type)) return false;
      radios.forEach(field => {
        field.checked = field.value === type;
        if (field.checked) field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const fields = Array.from(this.root.querySelectorAll('[name]'));
      // Set all values before emitting events, so fonts cannot overwrite restored placement.
      for (const field of fields) {
        const name = propertyName(field.name);
        if (!name || field.type === 'file' || field.type === 'radio' || name.startsWith('_')) continue;
        if (!Object.hasOwn(this.original, name)) continue;
        const value = String(this.original[name] ?? '');
        if (field.tagName === 'SELECT' && value && !Array.from(field.options).some(option => option.value === value)) return false;
        field.value = value;
      }
      fields.filter(field => field.type !== 'file' && field.type !== 'radio').forEach(field => {
        field.dispatchEvent(new Event(field.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
      });
      this.root.dispatchEvent(new CustomEvent('je:personalization-restored', { bubbles: true }));
      this.lockFixedChoices();
      return true;
    }

    lockFixedChoices() {
      this.info.querySelectorAll('variant-selects input, variant-selects select, variant-selects button, [name="quantity"], [data-je-type], [data-je-file]').forEach(field => {
        field.disabled = true;
      });
      this.root.querySelectorAll('[data-je-file]').forEach(field => {
        const wrapper = field.closest('.je-field');
        if (wrapper) wrapper.hidden = true;
      });
      if (this.item) {
        this.info.querySelectorAll('[name="quantity"]').forEach(field => { field.value = this.item.quantity; });
      }
      // Existing personalization UI can re-enable controls while refreshing font previews.
      if (!this.fixedListener) {
        this.fixedListener = () => this.lockFixedChoices();
        this.root.addEventListener('change', this.fixedListener);
        this.root.addEventListener('je:personalization-restored', this.fixedListener);
      }
    }

    setFieldsBusy(busy) {
      if (busy) {
        this.busyFields = new Map();
        this.root.querySelectorAll('input, select, textarea, button').forEach(field => {
          this.busyFields.set(field, field.disabled);
          field.disabled = true;
        });
      } else {
        this.busyFields?.forEach((disabled, field) => { field.disabled = disabled; });
        this.busyFields = null;
      }
    }

    setButtons(disabled, text) {
      this.buttons.forEach(button => {
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        button.removeAttribute('aria-haspopup');
        const label = button.querySelector('span:not(.loading__spinner)');
        if (label) label.textContent = text;
        else button.textContent = text;
      });
    }

    block(key) {
      globalThis.JaxStorefrontAnalytics?.trackForm?.(this.form, 'edit_error', { error_code: 'design_load_failed' });
      this.mode = 'blocked';
      this.status.textContent = '';
      this.error.textContent = this.copy[key];
      this.error.hidden = false;
      this.setButtons(true, this.copy.save);
      this.error.focus({ preventScroll: true });
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    editableProperties() {
      const next = { ...this.original };
      const editable = new Set(Array.from(this.root.querySelectorAll('textarea[name], select[name], input[name]'))
        .filter(field => !field.disabled && field.type !== 'file' && field.type !== 'radio' && field.type !== 'hidden')
        .map(field => propertyName(field.name))
        .filter(name => name && !name.startsWith('_') && name !== 'Logo' && name !== 'Personalization type'));
      // Only known form fields may change. Hidden files and every unrelated property survive.
      for (const [name, value] of new FormData(this.form)) {
        const key = propertyName(name);
        if (editable.has(key) && typeof value === 'string' && (value !== '' || Object.hasOwn(next, key))) next[key] = value;
      }
      return next;
    }

    unchanged(item) {
      return item && String(item.variant_id) === String(this.item.variant_id)
        && String(item.product_id) === String(this.item.product_id)
        && item.quantity === this.item.quantity && planId(item) === planId(this.item)
        && fingerprint(item.properties) === fingerprint(this.original);
    }

    matches(item, properties) {
      return String(item.variant_id) === String(this.item.variant_id)
        && item.quantity === this.item.quantity && planId(item) === planId(this.item)
        && fingerprint(item.properties) === fingerprint(properties);
    }

    async save() {
      if (this.mode !== 'ready') return;
      // A scripted or stale variant change must never retarget the existing cart line.
      if (this.form.querySelector('[name="id"]')?.value !== String(this.item.variant_id)) return this.block('stale');
      const properties = this.editableProperties();
      if (fingerprint(properties) === fingerprint(this.original)) return this.complete();
      this.mode = 'saving';
      this.error.hidden = true;
      this.status.textContent = this.copy.saving;
      this.setButtons(true, this.copy.saving);
      this.setFieldsBusy(true);
      let mutationStarted = false;
      try {
        const cart = await this.request('cart.js');
        const fresh = cart.items?.find(item => item.key === this.key);
        if (!fresh) return this.block('missing');
        if (!this.unchanged(fresh)) return this.block('stale');
        // Shopify can combine identical variant/property lines. Keep this edit on its own line.
        if (cart.items.some(item => item.key !== this.key && String(item.variant_id) === String(this.item.variant_id)
          && planId(item) === planId(this.item) && fingerprint(item.properties) === fingerprint(properties))) {
          this.recover('collision');
          return;
        }
        mutationStarted = true;
        const updated = await this.request('cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: this.key, quantity: fresh.quantity, properties }),
        });
        if (!updated.items?.some(item => this.matches(item, properties))) return this.block('uncertain');
        this.complete();
      } catch (error) {
        if (!mutationStarted || error.rejected) {
          this.recover('save_error');
          return;
        }
        // A lost response is not proof of failure. Check the cart, never repeat a mutation blindly.
        try {
          const cart = await this.request('cart.js');
          if (cart.items?.some(item => this.matches(item, properties))) this.complete();
          else if (this.unchanged(cart.items?.find(item => item.key === this.key))) this.recover('save_error');
          else this.block('uncertain');
        } catch (_) {
          this.block('uncertain');
        }
      } finally {
        this.setFieldsBusy(false);
        this.lockFixedChoices();
      }
    }

    recover(key) {
      globalThis.JaxStorefrontAnalytics?.trackForm?.(this.form, 'edit_error', { error_code: 'design_save_failed' });
      this.mode = 'ready';
      this.status.textContent = '';
      this.error.textContent = this.copy[key];
      this.error.hidden = false;
      this.setButtons(false, this.copy.save);
      this.error.focus();
    }

    complete() {
      globalThis.JaxStorefrontAnalytics?.trackForm?.(this.form, 'edit_saved');
      this.mode = 'saved';
      this.status.textContent = this.copy.saved;
      this.setButtons(true, this.copy.saved);
      this.returnToCart();
    }

    returnToCart() {
      window.location.assign(this.dataset.cartUrl);
    }
  });
})();
